import React, {useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheckCircle, faExclamationTriangle} from '@fortawesome/free-solid-svg-icons';
import styles from './styles.module.scss';
import Image from 'next/image';
import {getDomain, getPathFromUrl} from '@/utils/url';
import {NextImg} from '@/utils/nextImg';
import {BannerStyled, ButtonStyled, RadioStyled, TabsContaier} from './style';
import {Tabs, Empty, Tooltip} from 'antd';
import {observer} from 'mobx-react';
import {useStore} from '@/store/root-store';
import {faArrowRight, faCircleInfo} from '@fortawesome/pro-regular-svg-icons';
import CardCollapse from './CardCollapse';

const {TabPane} = Tabs;
interface StepThreeProps {
  record: any;
  data: any;
  setVisible: any;
}


const Card = ({isSuccess, title, description}) => {
  return (
    <div className={isSuccess ? styles.successCard : styles.warningCard} style={{marginTop: 8}} aria-label='PageMod Verification'>
      <FontAwesomeIcon icon={isSuccess ? faCheckCircle : faExclamationTriangle} color={isSuccess ? '#2AC155' : '#F17B2C'} style={{marginTop: 2}} aria-label='Success' />
      <div>
        {title || description ? <>
          {title ? <div className={isSuccess ? styles.successTitle : styles.warningTitle}>{title}</div> : null}
          {description ? <div className={isSuccess ? styles.successDescription : styles.warningDescription}>{description}</div> : null}
        </> : null}
      </div>
    </div>
  );
};

const getStatusName = status => {
  if (status === 'wp_integration') return 'WordPress';
  if (status === 'js_integration') return 'JavaScript';
  if (status === 'gtm_integration') return 'Google Tag Manager';
  return 'Unknown';
};

const getFilteredDeploymentData = (groups, filter) => {
  if (!filter) return {groups, successCount: 0, errorCount: 0};

  const countUrlsByStatus = (groups, status) => {
    let count = 0;
    groups.forEach(group => {
      group.subGroups.forEach(subGroup => {
        count += subGroup.urls.filter(url => url.status === status).length;
      });
    });
    return count;
  };

  const successCount = countUrlsByStatus(groups, 'deploy');
  const errorCount = countUrlsByStatus(groups, 'failed');

  const filteredGroups = groups
    .map(group => {
      const filteredSubGroups = group.subGroups
        .map(subGroup => {
          let filteredUrls = [];
          if (filter === 'success') {
            filteredUrls = subGroup.urls.filter(url => url.status === 'deploy');
          } else if (filter === 'error') {
            filteredUrls = subGroup.urls.filter(url => url.status === 'failed');
          }
          if (filteredUrls.length === 0) return null;
          return {
            ...subGroup,
            urls: filteredUrls,
            fixedDeployments: filter === 'success' ? filteredUrls.length : 0,
            totalDeployments: filteredUrls.length,
          };
        })
        .filter(Boolean);
      if (filteredSubGroups.length === 0) return null;
      const groupTotal = filteredSubGroups.reduce((sum, sg) => sum + sg.totalDeployments, 0);
      return {
        ...group,
        subGroups: filteredSubGroups,
        fixedDeployments: filter === 'success' ? groupTotal : 0,
        totalDeployments: groupTotal,
      };
    })
    .filter(Boolean);

  return {groups: filteredGroups, successCount, errorCount};
};

const sortSubGroups = subGroups => {
  return [...subGroups].sort((a, b) => {
    if (a.totalDeployments === 0 && b.totalDeployments !== 0) return 1;
    if (a.totalDeployments !== 0 && b.totalDeployments === 0) return -1;
    if (a.totalDeployments === 0 && b.totalDeployments === 0) {
      if (a.urls.length === 0 && b.urls.length > 0) return 1;
      if (a.urls.length > 0 && b.urls.length === 0) return -1;
      return 0;
    }
    return 0;
  });
};
const isZeroDeploymentGroup = group => group.subGroups.every(sg => sg.totalDeployments === 0);
const sortGroups = groups => {
  return [...groups].sort((a, b) => {
    const aZero = isZeroDeploymentGroup(a);
    const bZero = isZeroDeploymentGroup(b);
    if (aZero && !bZero) return 1;
    if (!aZero && bZero) return -1;
    return 0;
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StepThreeComponent: React.FC<StepThreeProps> = ({record, data, setVisible}) => {
  const domainName = record?.hostname;
  const [activeKey, setActiveKey] = useState<string>('inspection');
  const [selectedStatus, setSelectedStatus] = useState<'success' | 'error' | null>(null);
  const {settings: {customer: {profile: {whitelabelOtto}}}} = useStore('');
  const integrations = data?.integrationsFound;
  const correctInstallationMethod = integrations && integrations?.some(item => ['installed', 'wp_plugin'].includes(item?.state));
  const installationMethods = data?.integrationsFound?.filter(item => ['installed', 'wp_plugin'].includes(item?.state))?.map(item => getStatusName(item?.integration));
  const installationMethodsString = installationMethods?.join(', ');
  const incorrectInstallationMethod = data?.integrationsFound?.filter(item => !['installed', 'wp_plugin'].includes(item?.state))?.map(item => getStatusName(item?.integration));
  const incorrectInstallationMethodString = incorrectInstallationMethod?.join(', ');
  const reviewHereLink = (
    <span style={{color: '#2D6CCA', cursor: 'pointer', textDecoration: 'underline'}} onClick={() => setActiveKey('otto_deployments')}>
      here
    </span>
  );

  const ottoGroups = data?.ottoDeployments?.groups || [];
  const {groups: filteredOttoGroups} = getFilteredDeploymentData(ottoGroups, selectedStatus);

  const sortedGroups = sortGroups((selectedStatus ? filteredOttoGroups : ottoGroups).map(group => {
    return {
      ...group,
      subGroups: sortSubGroups(group.subGroups || []),
    };
  }));

  const urlsScanned= data?.urlsScanned?.filter(item => data?.givenUrl !== item);

  return (
    <div className={styles.wrapper} aria-label='Site Info Diagnostic Results'>
      <div className={styles.stepThree}>
        {/* Header */}
        <div style={{paddingInline: 16}}>
          <div className={styles.title} style={{marginBottom: 17}}>
            <span aria-label='Site Info' tabIndex={0}>
              <Image src={'/img/DiagnosticsGlobe.svg'} alt='Site Info' width={60} height={60}/>
            </span>
            <div>
              <h1 className={styles.siteInfo}>Site Info</h1>
              <p className={styles.siteDescription}>Technical details and verification results</p>
            </div>
          </div>
          {/* Website URL */}
          <div className={styles.urlInputWrapper} aria-label='Website URL'>
            <div className={styles.urlDomainWrapper} style={{width: '100%'}}>
              <span className={styles.searchedUrl}>Searched URL:</span>
              <NextImg style={{height: '20px', width: '20px', borderRadius: '4px', marginRight: 5}} src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${domainName && getDomain((!/^(https?:)?\/\//i.test(domainName)) ? 'https:' + domainName : domainName)}&size=64`}/>
              <Tooltip title={data?.givenUrl}>
                <a className={styles.urlDomainText} href={data?.givenUrl} target='_blank' rel='noopener noreferrer'>{data?.givenUrl}</a>
              </Tooltip>
            </div>
            {!urlsScanned?.length ? null :
              <div className={styles.sampleUrlsContainer}>
                <div className={styles.sampleUrlsTitle}>Sample URL:
                  <Tooltip title={'This tool checks multiple page paths to ensure the pixel and page modifications are working correctly across your site.'} overlayClassName={styles.tooltipStyle}><FontAwesomeIcon icon={faCircleInfo} color='#AAAAAA' style={{width: 16, height: 16, cursor: 'pointer'}} />
                  </Tooltip></div>
                <div className={styles.sampleUrls}>
                  {urlsScanned?.map(urlScan => <a className={styles.sampleUrl} href={urlScan} target='_blank' rel='noopener noreferrer' key={urlScan}>{getPathFromUrl(urlScan)}</a>)}
                </div>
              </div>}
          </div>
        </div>
        {activeKey === 'otto_deployments' && <BannerStyled>
          <FontAwesomeIcon icon={faCircleInfo} style={{width: 16, height: 16}} color='#2D6CCA' />
          <p>We use JavaScript rendering to identify deployed fixes—just like Google does.</p>
          <ButtonStyled>
            <p className={styles.textStyled} onClick={() => window.open('https://searchatlas.com/blog/google-javascript-update/', '_blank')}>Learn more</p>
            <FontAwesomeIcon icon={faArrowRight} color='#2D6CCA' />
          </ButtonStyled>
        </BannerStyled>}
        <TabsContaier >
          <Tabs className='modal-tabs' activeKey={activeKey} onChange={setActiveKey}>
            { <TabPane tab={`Inspection`} key={'inspection'} >
              <div className={styles.scrollContainer} style={{paddingBottom: 24, paddingInline: 16}}>
                <div className={styles.stepThreeLabel} style={{marginTop: 16, marginBottom: 8}}>Installation Methods</div>
                <div className={styles.section}>

                  {correctInstallationMethod ? <Card isSuccess title='Correct Installation Method' description={`The ${whitelabelOtto} pixel is correctly installed via ${installationMethodsString}.`} />:
                    <Card isSuccess={false} title={data?.integrationsFound?.length >= 1 ? 'Incorrect Installation Method' : 'No Installation Method Found'}
                      description={data?.integrationsFound?.length >= 1 ?
                        <span>The {whitelabelOtto} pixel is incorrectly installed via {incorrectInstallationMethodString}. Please click <a href={`/seo-automation/installation-guide?uuid=${record?.uuid}`} target='_blank' rel='noopener noreferrer' style={{color: '#2D6CCA', textDecoration: 'underline'}}>here </a>
                        and follow the installation guide.</span> :
                        <span>No installation method was found.</span>} />}

                  <Card isSuccess={installationMethods?.length > 1 ? false : true} title='Duplicate Installation Check' description={installationMethods?.length > 1 ?
                    <span>{installationMethods?.length} instances of {whitelabelOtto} pixel found. Please remove the duplicate implementation.</span> :
                    <span>No duplicated instance of {whitelabelOtto} pixel was detected.</span>} />
                </div>
                <div className={styles.section}>
                  <div className={styles.stepThreeLabel} style={{marginTop: 24, marginBottom: 8}}>Page Modifications</div>
                  <Card isSuccess={data?.pageMod} title='Deployed Fixes Verification' description={data?.pageMod ? 'All page modifications are correctly implemented and functioning' :
                    <span>Some of deploys were not identified in our scan, please review them {reviewHereLink}.</span>} />
                </div>
              </div>
            </TabPane>}
            <TabPane tab={`${whitelabelOtto} Deployments`} key={'otto_deployments'}>
              <div style={{marginTop: 8, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px'}}>
                <ButtonStyled
                  background={selectedStatus === 'success' ? '#1FAC47' : '#fff'}
                  style={{height: 37, gap: 8, width: '50%'}}
                  onClick={() => setSelectedStatus(prev => prev === 'success' ? null : 'success')}
                >
                  <RadioStyled checked={selectedStatus === 'success'} success />
                  <p className={styles.numberStyled} style={{color: selectedStatus === 'success' ? '#fff' : 'black'}}>{data?.ottoDeployments?.success || 0}</p>
                  <p className={styles.statusText} style={{color: selectedStatus === 'success' ? '#fff' : 'black'}}>Successful</p>
                </ButtonStyled>
                <ButtonStyled
                  background={selectedStatus === 'error' ? '#F44343' : '#fff'}
                  style={{height: 37, gap: 8, width: '50%'}}
                  onClick={() => setSelectedStatus(prev => prev === 'error' ? null : 'error')}
                >
                  <RadioStyled checked={selectedStatus === 'error'} />
                  <p className={styles.numberStyled} style={{color: selectedStatus === 'error' ? '#fff' : 'black'}}>{data?.ottoDeployments?.failed || 0}</p>
                  <p className={styles.statusText} style={{color: selectedStatus === 'error' ? '#fff' : 'black'}}>Errors</p>
                </ButtonStyled>
              </div>
              <div style={{paddingBottom: 65, maxHeight: 500, overflowY: 'auto', paddingInline: 16, background: 'rgba(238, 238, 238, 1)'}}>
                {(() => {
                  if (!sortedGroups || sortedGroups.length === 0 || sortedGroups.every(g => !g.subGroups || g.subGroups.length === 0)) {
                    return (
                      <div style={{padding: 24, background: '#fff', borderRadius: 8, marginTop: 12, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description='No Deployments Found'
                        />
                      </div>
                    );
                  }
                  return sortedGroups.map(group => (
                    <CardCollapse
                      key={group.name}
                      group={group}
                      isZeroDeploymentGroup={isZeroDeploymentGroup(group)}
                      defaultExpanded={sortedGroups.length === 1}
                      setVisible={setVisible}
                    />
                  ));
                })()}
              </div>
            </TabPane>
          </Tabs>
        </TabsContaier>

      </div>
    </div>
  );
};

export const StepThree = observer(StepThreeComponent);

